// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.ImportStore.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//
// </copyright>
// ----------------------------------------------------------------------

import { observable, action, computed } from 'mobx';
import Import from 'logic/models/Izenda.Import';
import ImportDataModel from 'logic/models/Izenda.ImportDataModel';
import fetcher from 'fetchers/Izenda.DataModelDataFetcher';
import importFetcher from 'fetchers/Izenda.ImportFetcher';
import tenantFetcher from 'fetchers/Izenda.TenantManagementDataFetcher';
import connectionStringDataFetcher from 'fetchers/Izenda.ConnectionStringDataFetcher';
import workspaceDataFetcher from 'fetchers/Izenda.WorkspaceDataFetcher';
import QuerySourceTree from 'logic/models/Izenda.QuerySourceTreeMultiLevel';
import accessDataFetcher from 'fetchers/Izenda.AccessDataFetcher';
import AppStore from 'logic/Izenda.AppStore';
import reportsDataFetcher from 'fetchers/Izenda.ReportsDataFetcher';
import dashboardDataFetcher from 'fetchers/Izenda.DashboardDataFetcher';
import { concatArray } from 'share/utils/Izenda.LazyLoadingUtils';
import uiState from 'logic/Izenda.UiState';
import {
  forEach,
  find,
  isEmpty,
  filter,
  some,
  map,
  includes,
  get,
  extend,
  remove,
  reduce,
  endsWith,
  groupBy,
  flow,
  findIndex,
  findLastIndex,
  toLower,
  concat,
  isNil
} from 'lodash/fp';
import { LAZY_LOADING_DEFAULT_ITEMS_NUMBER } from 'logic/data/Izenda.UIConstants';
import ImportFileContent from './models/Izenda.ImportFileContent';
import localizer from 'share/localization/Izenda.Localizer';
import tracker from 'share/tracking/Izenda.Tracker';
import {
  FILE_EXTENSION,
  TYPE_REPORT,
  TYPE_DASHBOARD,
  ImportStatus as IMPORT_STATUS
} from 'logic/data/Izenda.ImportConstants';

@tracker
export default class ImportStore {
  @observable model: Import = null;
  @observable saveInto: Array<any> = null;
  @observable dataModelImport: ImportDataModel = null;

  reportLocalCategories: Array<any> = { categories: observable([]), isLastPage: false };
  templateLocalCategories: Array<any> = { categories: observable([]), isLastPage: false };
  reportGlobalCategories: Array<any> = { categories: observable([]), isLastPage: false };
  templateGlobalCategories: Array<any> = { categories: observable([]), isLastPage: false };

  dashboardLocalCategories: Array<any> = { categories: observable([]), isLastPage: false };
  dashboardGlobalCategories: Array<any> = { categories: observable([]), isLastPage: false };

  @observable databaseNamesTo: Array<any> = null;
  @observable showErrorMessages: boolean = false;

  @observable hasErrorOnServerSideReportTab: boolean = false;
  @observable hasErrorOnClientSideReportTab: boolean = false;
  @observable hasErrorOnServerSideDashboardTab: boolean = false;
  @observable hasErrorOnClientSideDashboardTab: boolean = false;

  tempSubCategories: Array<any> = [];

  errorMessages: Array<any> = [];

  constructor() {
    this.model = new Import();
    this.dataModelImport = new ImportDataModel();
    this.initial();
    this.didOverwriteHandles = false;
  }

  @computed
  get reportTabTitle() {
    return (
      localizer.getText('COMMON_LABEL_REPORT') +
      (isEmpty(this.model.reports) ? '' : ' (' + this.model.reports.length + ')')
    );
  }

  @computed
  get dashboardTabTitle() {
    return (
      localizer.getText('COMMON_LABEL_DASHBOARD') +
      (isEmpty(this.model.dashboards) ? '' : ' (' + filter({ isReport: false }, this.model.dashboards).length + ')')
    );
  }

  @computed
  get hasErrorReportTab() {
    return this.hasErrorOnServerSideReportTab || this.hasErrorOnClientSideReportTab;
  }

  @computed
  get hasErrorDashboardTab() {
    return this.hasErrorOnServerSideDashboardTab || this.hasErrorOnClientSideDashboardTab;
  }

  @computed
  get hasDataModelLoaded() {
    return this.dataModelImport.loaded;
  }

  @computed
  get hasFileLoaded() {
    return (!isEmpty(this.model.reports) || !isEmpty(this.model.dashboards)) && !this.showErrorMessages;
  }

  /**
   * Initial data
   */
  @action
  initial(): void {
    AppStore.loadCategorySettings()
      .bind(this)
      .then(
        action(result => {
          this.saveInto = result;
        })
      );
    this.getDatabaseNamesTo();
    uiState.setCheckDirtyFunction(
      this.checkDirty,
      this.onOkDirtyForm.bind(this),
      'IMPORT_CONFORM_REMOVE_ALL_FILE'
    );
  }

  /**
   * Reset data
   */
  @action
  reset(): void {
    this.resetDirty();
    this.model = new Import();
    this.dataModelImport = new ImportDataModel();
    this.saveInto = null;

    this.resetCategories(this.reportLocalCategories);
    this.resetCategories(this.reportGlobalCategories);
    this.resetCategories(this.templateLocalCategories);
    this.resetCategories(this.templateGlobalCategories);
    this.resetCategories(this.dashboardLocalCategories);
    this.resetCategories(this.dashboardGlobalCategories);

    this.resetArray(this.databaseNamesTo);
    this.errorMessages = [];
    this.showErrorMessages = false;

    this.hasErrorOnClientSideReportTab = false;
    this.hasErrorOnServerSideReportTab = false;
    this.hasErrorOnClientSideDashboardTab = false;
    this.hasErrorOnServerSideDashboardTab = false;

    this.resetArray(this.tempSubCategories);

    this.initial();
  }

  /**
   * Reset categories
   * @param {Object} category
   */
  resetCategories(category: any): void {
    this.resetArray(category.categories);
    category.isLastPage = false;
  }

  /**
   * Reset array
   * @param {Array} arr
   */
  resetArray(arr: Array<any>): void {
    !isEmpty(arr) && arr.splice(0, arr.length);
  }

  /**
   * Populate content for import page
   * @param {Object} content
   */
  @action
  createImportContent(content: Import): void {
    const dataModel = get('dataModels[0]', content);
    if(dataModel && uiState.isSingleTenantMode && uiState.currentUser.systemAdmin){
      uiState.setTenantId(null, false, true);
      this.getAllNameTenantList().then(tenantList => {
        this.getSourceConnectionsFromImportDataModel(dataModel.id).then(sourceConnections => {
          this.dataModelImport.create(dataModel, sourceConnections, tenantList);
        });
      });
    }else{
      const localCategoryId = find({ isGlobal: false }, this.saveInto).id;
      this.model.create(content, localCategoryId);
      this.showErrorMessages = !isEmpty(this.model.invalidFileMessages) || content.hasInvalidFile;
      this.loadInitialCategories();
      this.getDatabaseNamesTo();
    }
    this.markDirty();
  }

  /**
   * Append data
   * @param {Object} content
   */
  @action
  appendContent(content: Import): void {
    const importSourceCat = get('model.importSourceCat', this);
    const localCategoryId = find({ isGlobal: false }, this.saveInto).id;
    const newFiles = this.model.appendContent(content, localCategoryId);
    this.loadInitialCategories();
    if (importSourceCat) {
      this.updateSourceCategoryForImportFiles(newFiles);
    }
  }

  /**
   * Get data for database names
   */
  @action
  getDatabaseNamesTo(): void {
    // Get all items, not for lazyloading
    const requestData = {
      skipItems: -1,
      tenantId: uiState.tenantId
    };

    fetcher
      .getConnectionInfo(requestData)
      .bind(this)
      .then(
        action(response => {
          this.databaseNamesTo = response.result;
        })
      );
  }

  /**
   * Load initial data for categories
   */
  @action
  loadInitialCategories(): void {
    const data = [...this.model.reports, ...this.model.dashboards];
    forEach(d => {
      if (d.isReport) {
        this.processReportCategories(d);
      } else {
        this.processDashboardCategories(d);
      }
    }, data);
  }

  /**
   * Process report categories
   * @param {Object} report
   * @return {Promise}
   */
  @action
  processReportCategories(report: ImportFileContent): Promise {
    const contentType = get('content.type', report);
    const category = this.getReportCategoriesInfo(report.content, contentType);
    if (isEmpty(category.categories)) {
      return this.loadReportTemplateCategories(report);
    } else {
      report.categories = category.categories;
      report.isLastPage = category.isLastPage;
      return Promise.resolve();
    }
  }

  /**
   * Process dashboard categories
   * @param {Object} dashboard
   * @return {Promise}
   */
  @action
  processDashboardCategories(dashboard: ImportFileContent): Promise {
    const category = this.getDashboardCategoriesInfo(dashboard.content);
    if (isEmpty(category.categories)) {
      return this.loadDashboardCategories(dashboard);
    } else {
      dashboard.categories = category.categories;
      dashboard.isLastPage = category.isLastPage;
      return Promise.resolve();
    }
  }

  /**
   * Import the files
   * @param {Array<any>} overwriteOption
   * @return {Promise}
   */
  beginImport(overwriteOption: Array<any>): Promise {
    const data = this.model.buildData();

    // Do not import non overwrite files
    const removeFunction = remove(report => {
      const matchedItem = find(({ fileKey }) => fileKey === report.id, overwriteOption);
      return !!matchedItem && !matchedItem.isOverwrite;
    });

    data.reports = removeFunction(data.reports);
    data.dashboards = removeFunction(data.dashboards);

    // Do not call import API when there is no file to import
    if (isEmpty(data.reports) && isEmpty(data.dashboards)) {
      return Promise.resolve([]);
    }

    // Append overwrite option
    const postData = extend(data, {
      overwriteOption: reduce(
        (option, item) => extend(option, { [item.destinationId]: item.isOverwrite }),
        {},
        overwriteOption
      )
    });
    return importFetcher.importFiles(postData);
  }

  /**
   * Finish import, clean up files
   * @param {Array} importFiles
   */
  @action
  finishImport(importFiles: Array<any>) {
    const completedFileIds = flow(
      filter(({ status }) => status === IMPORT_STATUS.SUCCESS.value || status === IMPORT_STATUS.FAILED.value),
      map('fileKey')
    )(importFiles);

    this.model.removeCompletedFiles(completedFileIds);
    return this.updateDatabaseMappings();
  }

  /**
   * Update database mappings
   */
  @action
  updateDatabaseMappings() {
    return this.getDatabaseMappings().then((databaseMappings: Array<any>) => {
      isEmpty(this.model.reports) && isEmpty(this.model.dashboards) && this.reset();
      for (var i = 0; i < this.model.databaseMappings.length; i++) {
        const { fromDatabaseName, fromSchema, serverType } = this.model.databaseMappings[i];
        if (
          !some(
            currentMapping =>
              toLower(fromDatabaseName) === toLower(currentMapping.fromDatabaseName) &&
              toLower(fromSchema) === toLower(currentMapping.fromSchema) &&
              toLower(serverType) === toLower(currentMapping.serverType),
            databaseMappings
          )
        ) {
          this.updateErrorMessages(i, ['toDatabaseName'], '');
        }
      }

      this.model.updateDatabaseMappings(databaseMappings);
    });
  }

  /**
   * Cancel import progress
   * @param {Array} data
   */
  cancelImport(data: Array<any>) {
    return importFetcher.cancelImport(data);
  }

  /**
   * Get database mappings
   */
  @action
  getDatabaseMappings() {
    return importFetcher.getDatabaseMappings(this.model.fileIds);
  }

  /**
   * Get temporary report categories info.
   * @param {Object} report
   * @returns {Object} category
   */
  getReportCategoriesInfo({ isGlobal, type }): any {
    if (isGlobal) {
      if (type === 0) {
        return this.reportGlobalCategories;
      } else {
        return this.templateGlobalCategories;
      }
    } else {
      if (type === 0) {
        return this.reportLocalCategories;
      } else {
        return this.templateLocalCategories;
      }
    }
  }

  /**
   * Get temporary dashboard categories info.
   * @param {Object} dashboard
   * @returns {Object} category
   */
  getDashboardCategoriesInfo({ isGlobal }): any {
    if (isGlobal) {
      return this.dashboardGlobalCategories;
    } else {
      return this.dashboardLocalCategories;
    }
  }

  /**
   * Lazy loading data for report or template categories
   * @param {Object} report
   * @param {Object} pageInfo
   * @return {Promise}
   */
  @action
  loadReportTemplateCategories(report: ImportFileContent): Promise {
    const contentType = get('content.type', report);
    const category = this.getReportCategoriesInfo(report.content, contentType);
    const saveIntoId = report.saveIntoId || find({ isGlobal: false }, this.saveInto).id;

    const request = {
      skipItems: -1,
      type: contentType,
      parentIds: [saveIntoId],
      tenantId: uiState.tenantId
    };

    report.categories = category.categories;

    return reportsDataFetcher
      .loadLazyLoadingAllowedSavingCategories(request)
      .bind(this)
      .then(
        action((response: any) => {
          concatArray(report.categories, response.data, 'id');
        })
      );
  }

  /**
   * Lazy loading for dashboard categories
   * @param {Object} dashboard
   * @param {Object} pageInfo
   * @return {Promise}
   */
  @action
  loadDashboardCategories(dashboard: ImportFileContent): Promise {
    const saveIntoId = dashboard.saveIntoId || find({ isGlobal: false }, this.saveInto).id;
    const request = {
      skipItems: -1,
      type: dashboard.content.type,
      includeUncategory: false,
      parentIds: [saveIntoId],
      tenantId: uiState.tenantId
    };

    return dashboardDataFetcher.loadLazyLoadCategories(request).then(
      action((response: any) => {
        const category = this.getDashboardCategoriesInfo(dashboard.content);
        concatArray(category.categories, response.data, 'id');
        dashboard.categories = category.categories;
      })
    );
  }

  /**
   * Update name
   * @param {Object} importFileContent
   * @param {String} name
   */
  @action
  updateName(importFileContent: ImportFileContent, name: string): void {
    importFileContent.content.name = name;
    this.updateErrorMessages(importFileContent.content.index, ['category', 'subCategory'], this.model.activeTabKey);
  }

  /**
   * Update save into id
   * @param {Object} importFileContent
   * @param {String} saveIntoId
   */
  @action
  updateSaveIntoId(importFileContent: ImportFileContent, saveIntoId: string): void {
    const importSourceCat = get('model.importSourceCat',this);
    const loadCategoryFunc = importFileContent.isReport
                              ? this.processReportCategories
                              : this.processDashboardCategories;

    importFileContent.updateSaveIntoId(saveIntoId);
    importFileContent.content.isGlobal = find(category => category.id === saveIntoId, this.saveInto).isGlobal;
    importFileContent.content.categoryId = null;
    importFileContent.content.subCategoryId = null;
    this.resetArray(importFileContent.subCategories);
    this.updateErrorMessages(
      importFileContent.content.index,
      this.model.activeTabKey === 'report' ? ['category', 'subCategory'] : ['saveInto'],
      this.model.activeTabKey
    );

    const processSourceCatFunc = (importFileContent) => {
      if (importSourceCat) {
        this.updateSourceCategoryForImportFiles([importFileContent]);
      }
    };

    loadCategoryFunc.call(this, importFileContent).then(() => {
      processSourceCatFunc(importFileContent);
    });
  }

  /**
   * Update category
   * @param {Object} importFileContent
   * @param {String} categoryId
   * @param {String} categoryName
   */
  @action
  updateCategory(importFileContent: ImportFileContent, categoryId: string, categoryName: string) {
    importFileContent.updateCategory(categoryId, categoryName);
    this.resetArray(importFileContent.subCategories);
    importFileContent.updateSubCategory(null, null);
    this.updateErrorMessages(importFileContent.content.index, ['category', 'subCategory'], this.model.activeTabKey);

    if (importFileContent.isReport) {
      return this.loadReportTemplateSubCategories(importFileContent);
    } else {
      return this.loadDashboardSubCategories(importFileContent);
    }
  }

  /**
   * Lazy loading for report or template sub-category
   * @param {Object} report
   * @param {Object} pageInfo
   * @param {Boolean} isReset
   */
  @action
  loadReportTemplateSubCategories(report: ImportFileContent) {
    const tempSubCategories = this.getTempSubCategories(report);

    if (!String.isGuidFormat(report.content.categoryId)) {
      report.subCategories = tempSubCategories;
      return Promise.resolve();
    } else {
      const request = {
        skipItems: -1,
        type: report.content.type,
        parentIds: [report.saveIntoId, report.content.categoryId],
        tenantId: uiState.tenantId
      };

      return reportsDataFetcher.loadLazyLoadAllowedSavingSubCategories(request).then(
        action((response: any) => {
          report.subCategories = concat(response.data, tempSubCategories);
        })
      );
    }
  }

  /**
   * Lazy loading for dashboard sub-category
   * @param {Object} dashboard
   * @param {Object} pageInfo
   */
  @action
  loadDashboardSubCategories(dashboard: ImportFileContent): void {
    const tempSubCategories = this.getTempSubCategories(dashboard);

    if (!String.isGuidFormat(dashboard.content.categoryId)) {
      dashboard.subCategories = tempSubCategories;
      return Promise.resolve();
    } else {
      const request = {
        skipItems: -1,
        type: dashboard.content.type,
        includeUncategory: false,
        parentIds: [dashboard.saveIntoId, dashboard.content.categoryId],
        tenantId: uiState.tenantId
      };

      return dashboardDataFetcher.loadLazyLoadSubcategories(request).then(
        action((response: any) => {
          dashboard.subCategories = concat(response.data, tempSubCategories);
        })
      );
    }
  }

  /**
  * Check/uncheck import category checkbox
  */
  @action
  toggleImportCategory() {
    this.model.toggleImportCategory();
    this.processImportSourceCategory();
  }

  /**
  * To process the category when user cick Import Source Category
  */
  @action
  processImportSourceCategory() {
    const { reports, dashboards, importSourceCat } = this.model;
    const files = [...reports,...dashboards];
    if (importSourceCat) {
      this.updateSourceCategoryForImportFiles(files);
    } else {
      forEach(importFileContent => {
        this.removeNewCategory(importFileContent);
        this.updateCategory(importFileContent);
      }, files);
    }
  }


  /**
  * process the source category for a report/dashboard imported
  * @param {Object} importFileContent: file will be imported
  */
  @action
  updateSourceCategoryForImportFiles(importFileContents: any) {
    forEach(importFileContent => {
      const originalCategoryName = get('content.originalCategoryName', importFileContent);
      const catSelected = !isEmpty(originalCategoryName) ? find((cat)=> {
        return !isEmpty(cat.name)
                && cat.name.toLowerCase() == originalCategoryName.toLowerCase();
      } , importFileContent.categories) : null;
      this.populateImportSourceCategory(importFileContent, catSelected);
    }, importFileContents);
  }

  /**
  * if checkbox import source ategory is selected
  * @param {Object} importFileContent: file will be imported
  * @param {Object} catSelected: category may be existing on destination
  */
  @action
  populateImportSourceCategory(importFileContent: ImportFileContent, catSelected: any) {
    const categoryId = get('id', catSelected);
    const originalSubCategoryName = get('content.originalSubCategoryName',importFileContent);
    const originalCategoryName = get('content.originalCategoryName',importFileContent);
    // if the category from source is the same category from destination
    if (!isNil(catSelected)) {
      this.updateCategory(importFileContent, categoryId, originalCategoryName).then(
        () => {
          const subCategoryId = !isEmpty(originalSubCategoryName) ? flow(
            find((cat) => {
              return !isEmpty(cat.name)
                      && cat.name.toLowerCase() == originalSubCategoryName.toLowerCase();
            }),
            get('id')
          )(importFileContent.subCategories) : null;

          if (!isNil(subCategoryId)) { // if sub category is existing in destination
            this.updateSubCategory(importFileContent, subCategoryId, originalSubCategoryName);
          } else { // if destination does not have the category
            if (!isEmpty(originalSubCategoryName)) {
              this.populateNewSubCat(importFileContent, originalSubCategoryName);
            }
          }
        }
      );
    } else { // if the category from source which could not find in the destination
      if (!isEmpty(originalCategoryName)) {
        const newCat = {
          id: originalCategoryName,
          isNew: true,
          name: originalCategoryName
        };
        this.addNewCategory(importFileContent, newCat, true);
        if (!isEmpty(originalSubCategoryName)) {
          this.populateNewSubCat(importFileContent, originalSubCategoryName);
        }
      } else {
        this.updateCategory(importFileContent);
      }
    }
  }

   /**
  * To add new subcat and make this subcat populated
  * @param {Object} importFileContent: file will be imported
  * @param {string} subCatName: category name
  */
  populateNewSubCat(importFileContent: ImportFileContent, subCatName: any) {
    if (!isEmpty(subCatName)) {
      const newSubCat = {
        id: subCatName,
        isNew: true,
        name: subCatName
      };
      this.addNewSubCategory(importFileContent, newSubCat, true);
    }
  }
  /**
   * Load categories
   * @param {Object} importFileContent
   * @param {Object} pageInfo
   * @param {String} searchValue
   */
  @action
  loadCategories(importFileContent: ImportFileContent, pageInfo: any): Promise {
    const loadCategoriesFunc = importFileContent.isReport
      ? this.loadReportTemplateCategories
      : this.loadDashboardCategories;

    return loadCategoriesFunc.call(this, importFileContent, pageInfo);
  }

  /**
   * Load data for sub categories
   * @param {Object} importFileContent
   * @param {Object} pageInfo
   * @param {String} searchValue
   */
  @action
  loadSubCategories(importFileContent: ImportFileContent, pageInfo: any): Promise {
    const loadSubCategoriesFunc = importFileContent.isReport
      ? this.loadReportTemplateSubCategories
      : this.loadDashboardSubCategories;

    return loadSubCategoriesFunc.call(this, importFileContent, pageInfo);
  }

  /**
   * Update sub-category
   * @param {Object} importFileContent
   * @param {String} subCategoryId
   * @param {String} subCategoryName
   */
  @action
  updateSubCategory(importFileContent: ImportFileContent, subCategoryId: string, subCategoryName: string): void {
    importFileContent.updateSubCategory(subCategoryId, subCategoryName);
    this.updateErrorMessages(importFileContent.content.index, ['category', 'subCategory'], this.model.activeTabKey);
  }

  /**
   * Validate import's content
   */
  @action
  validate(): void {
    return importFetcher.validateImportContent(this.model.buildData()).then(result => {
      this.updateExistingErrorInEachTab(result.messages);
      this.errorMessages = result.messages;
      return Promise.resolve(result);
    });
  }

  /**
   * Get overwrite info tree
   */
  @action
  validateOverride(): void {
    return importFetcher.validateOverride(this.model.buildData());
  }

  /**
   * Update error messages
   * @param {Number} index
   */
  updateErrorMessages(index: number, columnKeys: Array<string>, tabKey: string): void {
    // Build keys need to update.
    const keys = map(
      columnKey => (isEmpty(tabKey) ? `${index}.${columnKey}` : `${tabKey}.${index}.${columnKey}`),
      columnKeys
    );

    // Get rows which are related together.
    const group = flow(
      find(message => message.key.indexOf(isEmpty(tabKey) ? `${index}.` : `${tabKey}.${index}.`) >= 0),
      get('detail')
    )(this.errorMessages);

    // Remove its row
    this.errorMessages = filter(message => !includes(message.key, keys), this.errorMessages);

    // After its row then getting remain error rows
    const remainErrorRows = filter(message => message.detail === group, this.errorMessages);

    // If it is last one, remove all rows which related together.
    if (remainErrorRows.length / columnKeys.length === 1) {
      this.errorMessages = filter(message => message.detail !== group, this.errorMessages);
    }

    // Update error icon for each tab
    this.updateExistingErrorInEachTab(this.errorMessages);
  }

  /**
   * Update 2 flag to check to exist error for each tab
   * @param {Array} errorMessages
   */
  @action
  updateExistingErrorInEachTab(errorMessages: Array<string>): void {
    this.hasErrorOnServerSideReportTab = some(message => message.key.indexOf('report') >= 0, errorMessages);
    this.hasErrorOnServerSideDashboardTab = some(message => message.key.indexOf('dashboard') >= 0, errorMessages);
  }
  /**
   * Get errors popup
   * @returns {Array}
   */
  @action
  getErrorsPopup(): Array<any> {
    return find({ key: 'dataModelConsistency' }, this.errorMessages).detail;
  }

  /**
   * Group valid files type
   * @type {Array}
   */
  groupByValidFiles(files: Array<any>): Array<any> {
    const checkIfDataModel = (fileName) => {
      return uiState.isSingleTenantMode && uiState.currentUser.systemAdmin && endsWith(FILE_EXTENSION.DATAMODEL, fileName);
    }

    return groupBy(
      file => endsWith(FILE_EXTENSION.REPORT, file.name) || endsWith(FILE_EXTENSION.DASHBOARD, file.name) || checkIfDataModel(file.name),
      files
    );
  }

  /**
   * Get invalid messages
   * @type {Array}
   */
  getInvalidMessages(files: Array<string>): Array<string> {
    return map(item => {
      const fileName = item.name.replace(/(^.*[\\\/]|\.[^/.]+$)/, '');
      return `${fileName} - ${localizer.getText('IMPORT_LABEL_INVALID_FILE_FORMAT')}`;
    }, files);
  }

  /**
   * Upload files
   * @type {Promise}
   */
  @action
  uploadFiles(files: Array<any>): any {
    const { true: validFiles, false: invalidFiles } = this.groupByValidFiles(files);
    this.model.invalidFileMessages = this.getInvalidMessages(invalidFiles);

    return importFetcher.uploadFiles(validFiles).then(content => {
      this.createImportContent(content);
      return content;
    });
  }

  /**
   * Hide error messages
   */
  @action
  hideErrorMessages() {
    this.showErrorMessages = false;
    this.resetArray(this.model.invalidFileMessages);
  }

  /**
   * Get schemas
   * @param {Object} data
   */
  @action
  getSchemas(databaseNameId: string): any {
    const requestData = {
      skipItems: -1,
      connectionId: databaseNameId
    };

    return fetcher.getSchemas(requestData).then(response => response.result);
  }

  /**
   * Remove importing files
   */
  removeFiles(fileIds: Array<string>): void {
    return importFetcher.removeFiles(fileIds);
  }

  /**
   * Remove importing files (sync)
   */
  removeFilesSync(): void {
    const fileIds = concat(this.model.fileIds, this.dataModelImport.fileId);
    !isEmpty(fileIds) && importFetcher.removeFilesSync(fileIds);
    this.resetDirty();
  }

  /**
   * Handle clean dirty form
   */
  onOkDirtyForm(): void {
    const fileIds = concat(this.model.fileIds, this.dataModelImport.fileId);
    !isEmpty(fileIds) && this.removeFiles(fileIds);
    this.reset();
  }

  /**
   * Check dirty form
   * @returns {boolean} A boolean value indicates dirty form status.
   */
  checkDirty = (): boolean => this.isDirty;

  /**
   * Update report list
   * @param {String} id
   * @param {Number} index
   */
  @action
  updateReports(id: string, index: number) {
    this.updateErrorMessages(index, ['category', 'subCategory'], this.model.activeTabKey);
    this.arrangeErrorRows(index, 1);
    this.model.arrangeIndex(index, 1, false);
    this.model.updateReports(id);
    if (isEmpty(this.model.reports)) {
      this.hasErrorOnClientSideReportTab = false;
      this.hasErrorOnServerSideReportTab = false;
    }
  }

  /**
   * Update dashboard list
   * @param {String} id
   * @param {Number} index
   */
  @action
  updateDashboards(id: string, index: number) {
    const start = findIndex({ id: id }, this.model.dashboards);
    const end = findLastIndex({ id: id }, this.model.dashboards);

    for (var i = start; i < end; i++) {
      this.updateErrorMessages(i, ['category', 'subCategory'], this.model.activeTabKey);
    }
    this.arrangeErrorRows(end, end - start + 1);
    this.model.arrangeIndex(index, end - start + 1, true);
    this.model.updateDashboards(id);
    if (isEmpty(this.model.dashboards)) {
      this.hasErrorOnClientSideDashboardTab = false;
      this.hasErrorOnServerSideDashboardTab = false;
    }
  }

  /**
   * Arrange error rows
   * @param {Number} index
   * @param {Number} step
   */
  arrangeErrorRows(index: number, step: number) {
    const filterByIndex = ({ key }) => {
      if (key.indexOf(`${this.model.activeTabKey}`) >= 0) {
        const rowIndex = key.substring(key.indexOf('.') + 1, key.lastIndexOf('.'));
        return parseInt(rowIndex, 10) > index;
      }
      return false;
    };

    const updateKey = (message: any) => {
      const { key } = message;
      const rowIndex = key.substring(key.indexOf('.') + 1, key.lastIndexOf('.'));
      const columnKey = key.substring(message.key.lastIndexOf('.') + 1, key.length);
      message.key = `${this.model.activeTabKey}.${rowIndex - step}.${columnKey}`;
    };

    flow(filter(filterByIndex), forEach(updateKey))(this.errorMessages);
  }

  /**
   * Get file's info to use fetch to get file's detail
   * @param {ImportFileContent} data
   * @returns {any}
   */
  @action
  getFileInfo(data: ImportFileContent): any {
    const type = this.model.activeTabKey === 'report' ? TYPE_REPORT : TYPE_DASHBOARD;
    const fileId = data.id;
    const objectId = data.isReport && this.model.activeTabKey === 'dashboard' ? data.content.id : '';

    return { type, fileId, objectId };
  }

  /**
   * Get detail file and load access rights to show on popup
   * @param {Object} any
   * @param {number} typeAccess
   * @returns {Array}
   */
  getDetailFile(fileInfo: any, typeAccess: number): Array<any> {
    return Promise.all([importFetcher.getDetailFile(fileInfo), accessDataFetcher.loadAccessRight(typeAccess)]);
  }

  /**
   * Remove new category
   * @param {Object} importFileContent
   * @param {Object} category
   */
  @action
  removeNewCategory(importFileContent: ImportFileContent): void {
    const { categories, subCategories } = importFileContent;
    const lastIndexFunc = (categories: any) => {
      return findLastIndex({isNew: true}, categories);
    };

    const removeCategoryNewStatusFunc = (cats: any) => {
      let lastIndex = lastIndexFunc(cats);
      while (lastIndex > -1) {
        cats.splice(lastIndex, 1);
        lastIndex = lastIndexFunc(cats);
      }
    };

    removeCategoryNewStatusFunc(categories);
    removeCategoryNewStatusFunc(subCategories);
    this.resetArray(this.tempSubCategories);
    this.updateCategory(importFileContent);
  }

  /**
   * Add new category
   * @param {Object} importFileContent
   * @param {Object} category
   * @param {Object} selected
   */
  @action
  addNewCategory(importFileContent: ImportFileContent, category: any, selected: boolean): void {
    const existingCategory = find({ name: category.name }, importFileContent.categories);

    if(existingCategory) {
      category.id = existingCategory.id;
    } else {
      category.id = 'new_' + category.id;
      importFileContent.categories.push(category);
    }

    if (selected) {
      this.updateCategory(importFileContent, category.id, category.name);
    } else {
      this.updateCategory(importFileContent);
    }
  }

  /**
   * Add new sub-category
   * @param {Object} importFileContent
   * @param {Object} subCategory
   * @param {Object} selected
   */
  @action
  addNewSubCategory(importFileContent: ImportFileContent, subCategory: any, selected: boolean): void {
    const existingSubCategory = find({ name: subCategory.name }, importFileContent.subCategories);

    if(existingSubCategory) {
      subCategory.id = existingSubCategory.id;
    } else {
      subCategory.id = 'new_' + subCategory.id;
      this.addForAllSubCategories(importFileContent, subCategory);
    }

    if (selected) {
      this.updateSubCategory(importFileContent, subCategory.id, subCategory.name);
    } else {
      this.updateSubCategory(importFileContent);
    }

    // When user enter to add a sub-category, it is cached which will use for loading sub-category data.
    this.tempSubCategories.push({
      saveIntoId: importFileContent.saveIntoId,
      categoryName: importFileContent.content.categoryName,
      isReport: importFileContent.isReport,
      subCategory: subCategory
    });
  }

  /**
   * Add sub category for all sub categories dropdown
   * @param {Object} importFileContent
   * @param {Object} subCategory
   */
  addForAllSubCategories({ saveIntoId, content: { categoryName }, isReport }, subCategory: any): void {
    flow(
      filter((importFileContent: ImportFileContent) => {
        return (
          importFileContent.saveIntoId === saveIntoId &&
          importFileContent.content.categoryName === categoryName &&
          importFileContent.isReport === isReport
        );
      }),
      map('subCategories'),
      forEach(subCategories => {
        subCategories.push(subCategory);
      })
    )([...this.model.reports, ...this.model.dashboards]);
  }

  /**
   * Get temporary sub-categories
   * @param {Object} importFileContent
   * @returns {Array} temporary sub-categories
   */
  getTempSubCategories(importFileContent: ImportFileContent): Array<any> {
    const subCategories =
      flow(
        filter({
          saveIntoId: importFileContent.saveIntoId,
          categoryName: importFileContent.content.categoryName,
          isReport: importFileContent.isReport
        }),
        map('subCategory')
      )(this.tempSubCategories) || [];

    return subCategories;
  }

  /**
   * GetAllNameTenantList
   */
  @action
  getAllNameTenantList(): any {
    return tenantFetcher.getAllNameTenantList();
  }

  /**
   * Get Source Connections From Import Data Model
   */
  @action
  getSourceConnectionsFromImportDataModel(fileId) {
     return workspaceDataFetcher.getSourceConnectionsFromImportDataModel({fileId: fileId});
  };

  /**
  * Go All Destination Connections for tenant
  */
  @action
  getAllDestinationConnections(tenant) {
    return connectionStringDataFetcher.getConnectionDetailInfo(tenant.tenantId, false, { removeEmptyParent: false });
  };

  /**
   * Validate Import Data Model
   */
  @action
  validateImportDataModel(data) {
     return workspaceDataFetcher.validateImportDataModel(data);
  };

    /**
   * Check Data Sources Conflicts When Import Data Model
   */
  @action
  checkDataSourcesConflictsWhenImportDataModel(data) {
     return workspaceDataFetcher.checkDataSourcesConflictsWhenImportDataModel(data)
     .then(result => {
        let tree = null;
        if(!isEmpty(result.overridenQuerySources)){
         const treeModel = map(tenant => {
           tenant.dbSource = {
             querySources: map(connection => {
              connection.querySources = connection.schemas;
              return connection;
             }, tenant.connections)
           };
           return tenant;
         }, result.overridenQuerySources);
         tree = new QuerySourceTree(treeModel);
        }
        return {
          success: result.success,
          tree: tree
        };
     });
  };

    /**
   * Import Data Model
   */
  @action
  importDataModel(data) {
     return workspaceDataFetcher.importDataModel(data);
  };
}



// WEBPACK FOOTER //
// ./ui/logic/Izenda.ImportStore.js